import { useState } from 'react'
import { Alert, Toggle, Chip } from '@/components/ui'
import { Lock } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Variable {
  key: string
  label: string
  descripcion: string
  usoCampanas: number
  activa: boolean
}

const moraInicial: Variable[] = [
  { key: 'días_mora', label: 'Días de mora', descripcion: 'Rango de días vencidos (min-max)', usoCampanas: 2, activa: true },
  { key: 'plaza', label: 'Plaza', descripcion: 'Ciudad / plaza del cliente', usoCampanas: 1, activa: true },
  { key: 'saldo_vencido', label: 'Saldo vencido', descripcion: 'Monto vencido (min-max USD)', usoCampanas: 0, activa: true },
  { key: 'cuotas_vencidas', label: 'Cuotas vencidas', descripcion: 'Cantidad de cuotas impagas', usoCampanas: 0, activa: true },
  { key: 'intentos_llamada', label: 'Intentos llamada', descripcion: 'Cuántas veces fue llamado por voz', usoCampanas: 0, activa: true },
  { key: 'genero', label: 'Género', descripcion: 'Sr / Sra', usoCampanas: 0, activa: false },
  { key: 'articulo_contiene', label: 'Artículo contiene', descripcion: 'Buscar por nombre del artículo', usoCampanas: 0, activa: false },
  { key: 'compromiso_previo', label: 'Compromiso previo', descripcion: '¿Tuvo compromiso incumplido?', usoCampanas: 0, activa: false },
  { key: 'segmento_cartera', label: 'Segmento cartera', descripcion: 'A, B, C según scoring interno', usoCampanas: 0, activa: false },
]

const prevInicial: Variable[] = [
  { key: 'dias_previa_vencimiento', label: 'Días previa vencimiento', descripcion: 'Días antes del vencimiento', usoCampanas: 1, activa: true },
  { key: 'monto_cuota', label: 'Monto cuota', descripcion: 'Monto cuota próxima a vencer', usoCampanas: 0, activa: true },
  { key: 'plaza', label: 'Plaza', descripcion: 'Ciudad / plaza del cliente', usoCampanas: 0, activa: true },
  { key: 'cuotas_restantes_max', label: 'Cuotas restantes máx', descripcion: 'Máx de cuotas que faltan', usoCampanas: 0, activa: true },
  { key: 'cliente_recurrente', label: 'Cliente recurrente', descripcion: '¿Es cliente antiguo?', usoCampanas: 0, activa: false },
  { key: 'score_crediticio', label: 'Score crediticio', descripcion: 'Scoring interno Point', usoCampanas: 0, activa: false },
  { key: 'plataforma_compra', label: 'Plataforma compra', descripcion: 'Online, tienda física, teléfono', usoCampanas: 0, activa: false },
]

export function CampanasVariables() {
  const [mora, setMora] = useState(moraInicial)
  const [prev, setPrev] = useState(prevInicial)

  const toggle = (setter: React.Dispatch<React.SetStateAction<Variable[]>>, key: string) =>
    setter(list => list.map(v => v.key === key && v.usoCampanas === 0 ? { ...v, activa: !v.activa } : v))

  return (
    <div className="space-y-5">
      <Alert
        variant="info"
        title="Qué es esto"
      >
        Elegís qué variables aparecen al crear una campaña, para no ver todas de una. <strong>Regla:</strong> si una variable está en uso por una campaña activa, no se puede desactivar (pero sigue filtrando).
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <VariablesCard
          titulo="Variables de Mora"
          color="rose"
          vars={mora}
          onToggle={k => toggle(setMora, k)}
        />
        <VariablesCard
          titulo="Variables de Preventiva"
          color="blue"
          vars={prev}
          onToggle={k => toggle(setPrev, k)}
        />
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <header className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-display text-lg text-navy-700">Variables en uso por campaña</h2>
          <p className="text-xs text-slate-500 mt-0.5">Si una campaña está activa, sus variables quedan bloqueadas.</p>
        </header>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Campaña</th>
              <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Tipo</th>
              <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Variables activas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-5 py-3 font-medium text-navy-700">Mora 15 días Guayaquil</td>
              <td className="px-5 py-3"><Chip variant="rose">Mora</Chip></td>
              <td className="px-5 py-3">
                <div className="flex flex-wrap gap-1">
                  {['días_mora', 'plaza', 'saldo_vencido', 'cuotas_vencidas', 'intentos_llamada'].map(k => (
                    <span key={k} className="px-2 py-0.5 bg-slate-100 rounded text-[11px] font-mono text-slate-600">{k}</span>
                  ))}
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-5 py-3 font-medium text-navy-700">Preventiva 1-5 días nacional</td>
              <td className="px-5 py-3"><Chip variant="blue">Preventiva</Chip></td>
              <td className="px-5 py-3">
                <div className="flex flex-wrap gap-1">
                  {['dias_previa_vencimiento', 'monto_cuota', 'plaza'].map(k => (
                    <span key={k} className="px-2 py-0.5 bg-slate-100 rounded text-[11px] font-mono text-slate-600">{k}</span>
                  ))}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  )
}

function VariablesCard({ titulo, color, vars, onToggle }: {
  titulo: string
  color: 'rose' | 'blue'
  vars: Variable[]
  onToggle: (k: string) => void
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <header className={cn(
        'px-5 py-4 text-white',
        color === 'rose' ? 'bg-gradient-to-br from-rose-500 to-rose-600' : 'bg-gradient-to-br from-blue-500 to-blue-600'
      )}>
        <h2 className="font-display text-lg">{titulo}</h2>
        <p className="text-[11px] opacity-80 mt-0.5">{vars.filter(v => v.activa).length} activas · {vars.length} totales</p>
      </header>
      <div className="divide-y divide-slate-100">
        {vars.map(v => {
          const locked = v.usoCampanas > 0
          return (
            <div key={v.key} className={cn('px-5 py-3 flex items-center justify-between gap-3', !v.activa && 'opacity-50')}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-navy-700 font-semibold">{v.key}</span>
                  {locked && <Lock className="w-3 h-3 text-amber-500" />}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{v.descripcion}</div>
              </div>
              <div className="flex items-center gap-3">
                {locked && (
                  <Chip variant="amber" className="text-[10px]">
                    En uso · {v.usoCampanas} camp.
                  </Chip>
                )}
                <div className={cn(locked && 'cursor-not-allowed')} title={locked ? 'No se puede desactivar — en uso' : undefined}>
                  <Toggle on={v.activa} onChange={() => onToggle(v.key)} disabled={locked} />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
